import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, CalendarDays } from "lucide-react";
import { API, fmtPrice, SITE_API_KEY } from "../lib/store";
import { RateAlertForm } from "./RateAlertForm";

const SUGGESTIONS = [
  "How should I care for 22K gold?",
  "What are the 4Cs of a diamond?",
  "Do you buy or exchange old gold?",
  "Help me choose a bridal set",
];

const needsAdvisor = (t) => /\/book|773-262-4377|advisor|consultation|appointment/i.test(t);

const DEFAULT_WELCOME = "A very warm welcome. Ask me anything about jewelry, gold, diamonds, gemstones or their care — or how Regal Jewels can serve you.";
const INTENT_WELCOME = {
  occasion: "A very warm welcome. I understand you're shopping for an occasion — tell me the day and who it's for, and I'll point you to pieces made for celebration.",
  learning: "A very warm welcome. You came to learn — a wonderful reason. Ask me anything about gold, diamonds, gemstones or their care, and I'll share what jewelers know.",
  custom: "A very warm welcome. Dreaming of a custom design? Describe what you imagine — a stone, a style, a story — and I'll tell you how our studio brings it to life.",
  selling: "A very warm welcome. Considering selling or exchanging gold? Ask me how purity, weight and the day's rate decide its true worth — honesty first, always.",
  exploring: "A very warm welcome. You're exploring our collection — delightful. Ask me about any world, metal or stone, and I'll guide you to what's worth seeing.",
  browsing: "A very warm welcome. Browse at your ease — and if a question wanders in about jewelry, gold or diamonds, I'm right here.",
};

export function RegalChat() {
  const [msgs, setMsgs] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("rj_chat") || "[]"); } catch (e) { return []; }
  });
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  const sid = useMemo(() => {
    let s = localStorage.getItem("rj_chat_sid");
    if (!s) {
      s = `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("rj_chat_sid", s);
    }
    return s;
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem("rj_chat", JSON.stringify(msgs.slice(-40))); } catch (e) { console.debug("chat save:", e?.message); }
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [msgs]);

  const send = async (preset) => {
    const msg = (preset || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: msg }, { role: "regal", text: "" }]);
    try {
      const res = await fetch(`${API}/regal-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": SITE_API_KEY },
        body: JSON.stringify({ session_id: sid, message: msg }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const d = p.slice(6);
          if (d === "[DONE]") continue;
          try {
            const j = JSON.parse(d);
            if (j.delta) {
              setMsgs((m) => {
                const c = [...m];
                c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + j.delta };
                return c;
              });
            } else if (j.products?.length) {
              setMsgs((m) => {
                const c = [...m];
                c[c.length - 1] = { ...c[c.length - 1], products: j.products };
                return c;
              });
            } else if (j.rate_alert) {
              setMsgs((m) => {
                const c = [...m];
                c[c.length - 1] = { ...c[c.length - 1], rateAlert: j.rate_alert };
                return c;
              });
            }
          } catch (e) { console.debug("chat parse:", e?.message); }
        }
      }
    } catch (e) {
      setMsgs((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "regal", text: "Forgive me — I lost my connection for a moment. Please try again, or call us at 773-262-4377." };
        return c;
      });
    }
    setBusy(false);
  };

  const sendRef = useRef(send);
  sendRef.current = send;

  const confirmRateAlert = (i, txt) => {
    setMsgs((prev) => {
      const c = prev.map((mm, idx) => (idx === i ? { ...mm, rateAlertDone: true } : mm));
      return [...c, { role: "regal", text: txt }];
    });
  };

  // "Hey Regal" voice questions land here
  useEffect(() => {
    const h = (e) => { if (e.detail?.q) sendRef.current(e.detail.q); };
    window.addEventListener("rj-chat-ask", h);
    return () => window.removeEventListener("rj-chat-ask", h);
  }, []);

  const intent = localStorage.getItem("rj_intent") || "";

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="regal-chat-panel">
      <div ref={boxRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="regal-chat-messages">
        {msgs.length === 0 && (
          <>
            <p className="text-[13px] leading-relaxed bg-[#f4eddc] text-[#3f3a30] px-3.5 py-2.5 max-w-[92%]" data-testid="regal-chat-welcome" data-intent={INTENT_WELCOME[intent] ? intent : "none"}>
              {INTENT_WELCOME[intent] || DEFAULT_WELCOME}
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)} data-testid={`regal-chat-suggestion-${i}`}
                  className="text-[11px] border border-[#d8cdb5] hover:border-[#a8842c] text-[#5c5240] px-3 py-1.5 transition-colors text-left">
                  {s}
                </button>
              ))}
            </div>
          </>
        )}
        {msgs.map((m, i) => (
          <div key={i}>
            <div className={m.role === "user" ? "flex justify-end" : "flex"}>
              <p
                data-testid={m.role === "user" ? "regal-chat-msg-user" : "regal-chat-msg-regal"}
                className={
                  m.role === "user"
                    ? "text-[13px] leading-relaxed bg-[#171310] text-[#f3ead2] px-3.5 py-2.5 max-w-[88%] whitespace-pre-wrap"
                    : "text-[13px] leading-relaxed bg-[#f4eddc] text-[#3f3a30] px-3.5 py-2.5 max-w-[92%] whitespace-pre-wrap"
                }
              >
                {(m.text || "").replace(/\*\*?([^*]+)\*\*?/g, "$1") || (busy && i === msgs.length - 1 ? "…" : "")}
              </p>
            </div>
            {m.role === "regal" && m.products?.length > 0 && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-1" data-testid="regal-chat-products">
                {m.products.map((p) => (
                  <Link key={p.handle} to={`/product/${p.handle}`} className="w-[118px] shrink-0 border border-[#e0d5bb] bg-white hover:border-[#a8842c] transition-colors" data-testid="regal-chat-product-card">
                    {p.image && <img src={p.image} alt={p.title} loading="lazy" className="w-full h-[96px] object-cover bg-[#faf7f0]" />}
                    <span className="block p-1.5">
                      <span className="block text-[10px] leading-tight text-[#3f3a30] line-clamp-2">{p.title}</span>
                      <span className="block text-[10px] font-semibold text-[#8a6a1f] mt-0.5">{fmtPrice(p.price)}</span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
            {m.role === "regal" && m.rateAlert && !m.rateAlertDone && (
              <RateAlertForm defaults={m.rateAlert} onDone={(txt) => confirmRateAlert(i, txt)} />
            )}
            {m.role === "regal" && m.text && !busy && needsAdvisor(m.text) && (
              <Link to="/book" className="inline-flex items-center gap-1.5 mt-1.5 text-[10px] tracking-[0.15em] uppercase text-[#8a6a1f] border border-[#c9a24b]/60 px-2.5 py-1.5 hover:bg-[#c9a24b]/10 transition-colors" data-testid="regal-chat-book-btn">
                <CalendarDays size={11} /> Book a private advisor
              </Link>
            )}
          </div>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 border-t border-[#eee6d6] px-3 py-2.5 shrink-0">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about gold, diamonds, care..."
          className="flex-1 text-[13px] outline-none bg-transparent text-[#171310] placeholder:text-[#b3a88f]"
          data-testid="regal-chat-input"
        />
        <button type="submit" disabled={busy || !input.trim()} aria-label="Send message"
          className="h-8 w-8 flex items-center justify-center bg-[#171310] text-[#e9d9ac] disabled:opacity-40 hover:bg-[#3a2f1d] transition-colors"
          data-testid="regal-chat-send">
          <Send size={13} />
        </button>
      </form>
      <p className="text-[10px] text-center text-[#8f8571] pb-2 px-3 shrink-0">
        Prefer a person? <Link to="/book" className="text-[#8a6a1f] underline underline-offset-2">Book a private advisor</Link> · 773-262-4377
      </p>
    </div>
  );
}
